<script setup lang="ts">
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { loadUserProjects } from '@/api/RobVisionApi';
import { type VisionProject, VisionProjectType } from '@/models/RobVision';
import ProjectsTable from '@/components/robvision/ProjectsTable.vue';
import CreateVisionProjectDialog from '@/components/robvision/CreateVisionProjectDialog.vue';
import CreateCADProjectDialog from '@/components/robvision/CreateCADProjectDialog.vue';
import CreateImageProjectDialog from '@/components/robvision/CreateImageProjectDialog.vue';
import { useAlertsStore } from '@/store/AlertsStore';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const router = useRouter();
const loadingSpinnerStore = useLoadingSpinnerStore();
const alertsStore = useAlertsStore();

const { mdAndUp } = useDisplay();
const projects: Ref<VisionProject[]> = ref([]);
const projectsLoaded = ref(false);
const showCreateVisionProjectDialog = ref(false);
const createCADDialog = ref(false);
const createLabelPickingDialog = ref(false);
const createDepalletizingDialog = ref(false);
const currentProjectName = ref<string>('');
const showErrorMessage = ref(false);

const activeProjects = computed(() =>
  projects.value.filter((project) => !project.archived),
);
const archivedProjects = computed(() =>
  projects.value.filter((project) => project.archived),
);
// Collapsed by default so archived projects stay out of the way
const showArchived = ref(false);

async function fetchUserProjects() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    projects.value = await loadUserProjects(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err.message || 'Error fetching projects');
    alertsStore.error($gettext('Could not load Vision Projects'));
  } finally {
    projectsLoaded.value = true;
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

function onProjectCreated(uuid?: string) {
  resetDialogs();
  if (uuid) {
    // Jump straight into the freshly created project
    router.push(`/robvision/projects/${uuid}`);
  } else {
    fetchUserProjects();
  }
}

function resetDialogs() {
  createCADDialog.value = false;
  createLabelPickingDialog.value = false;
  createDepalletizingDialog.value = false;
  currentProjectName.value = '';
}

function openCreateDialog() {
  showCreateVisionProjectDialog.value = true;
}

function handleProjectTypeChoice(name: string, type: VisionProjectType) {
  showCreateVisionProjectDialog.value = false;
  currentProjectName.value = name;
  createCADDialog.value = false;
  createLabelPickingDialog.value = false;
  createDepalletizingDialog.value = false;
  if (type === VisionProjectType.CADPicking) {
    createCADDialog.value = true;
  } else if (type === VisionProjectType.LabelPicking) {
    createLabelPickingDialog.value = true;
  } else if (type === VisionProjectType.Depalletizing) {
    createDepalletizingDialog.value = true;
  }
}

function handleFileTooLarge() {
  showErrorMessage.value = true;
}

onMounted(async () => {
  await fetchUserProjects();
});
</script>

<template>
  <div class="pa-4 pa-sm-12 centered-page robvision-list">
    <v-row>
      <v-col cols="12">
        <h1>{{ $gettext('RobVision') }}</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="8">
        <p class="text-body-2 text-medium-emphasis">
          {{
            $gettext(
              'Each Vision Project teaches your robots to recognize and locate one part. Open a project below to train, deploy and refine its model — or start a new one with "Add Vision Project".',
            )
          }}
        </p>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12">
        <div class="d-flex align-end justify-space-between mb-2">
          <div>
            <h3>{{ $gettext('Vision Projects') }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-0">
              {{ $gettext('A list of all Vision Projects.') }}
            </p>
          </div>
          <v-tooltip anchor="bottom" location="bottom" :disabled="mdAndUp">
            <template v-slot:activator="{ props }">
              <span v-bind="props">
                <r-btn
                  :disabled="!mdAndUp"
                  variant="accent"
                  icon="mdi-plus"
                  @click="openCreateDialog"
                >
                  {{ $gettext('Add Vision Project') }}
                </r-btn>
              </span>
            </template>
            <span>
              {{
                $gettext(
                  'This is not supported on mobile devices. Please use a device with a larger screen',
                )
              }}
            </span>
          </v-tooltip>
        </div>
        <projects-table :projects="activeProjects" :loading="!projectsLoaded">
          <template #no-data>
            <div class="py-8 text-medium-emphasis">
              {{
                $gettext(
                  'No active projects yet. Create one with "Add Vision Project".',
                )
              }}
            </div>
          </template>
        </projects-table>
      </v-col>
    </v-row>

    <v-row v-if="archivedProjects.length > 0" class="mt-4">
      <v-col cols="12">
        <div
          class="d-flex align-center mb-2 archived-toggle"
          role="button"
          :aria-expanded="showArchived"
          tabindex="0"
          @click="showArchived = !showArchived"
          @keydown.enter="showArchived = !showArchived"
        >
          <v-icon
            :icon="showArchived ? 'mdi-chevron-down' : 'mdi-chevron-right'"
            size="small"
            class="mr-1"
          />
          <h4>{{ $gettext('Archived') }}</h4>
          <v-chip class="ml-2" size="small" density="comfortable">
            {{ archivedProjects.length }}
          </v-chip>
        </div>
        <v-expand-transition>
          <div v-show="showArchived">
            <projects-table :projects="archivedProjects" archived />
          </div>
        </v-expand-transition>
      </v-col>
    </v-row>
  </div>

  <!-- Archived project names may be reused (backend allows it), so the
       client-side duplicate check only considers active projects -->
  <create-vision-project-dialog
    v-model:isOpen="showCreateVisionProjectDialog"
    :existing-projects="activeProjects"
    @onTypeSelected="handleProjectTypeChoice"
  />
  <CreateCADProjectDialog
    v-if="currentProjectName"
    v-model:isCadOpen="createCADDialog"
    :project-name="currentProjectName"
    @part-created="onProjectCreated"
  />
  <create-image-project-dialog
    v-if="currentProjectName"
    v-model:isImageOpen="createLabelPickingDialog"
    :project-name="currentProjectName"
    :project-type="VisionProjectType.LabelPicking"
    @part-created="onProjectCreated"
    @file-too-large="handleFileTooLarge"
  />
  <create-image-project-dialog
    v-if="currentProjectName"
    v-model:isImageOpen="createDepalletizingDialog"
    :project-name="currentProjectName"
    :project-type="VisionProjectType.Depalletizing"
    @part-created="onProjectCreated"
    @file-too-large="handleFileTooLarge"
  />
  <v-snackbar
    v-model="showErrorMessage"
    :timeout="5000"
    color="error"
    location="top"
  >
    {{ $gettext('Filesize too large. Try again with smaller Image Files.') }}
  </v-snackbar>
</template>

<style scoped>
.archived-toggle {
  cursor: pointer;
  user-select: none;
  width: fit-content;
}
</style>
